import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Building2, ClipboardList, Users2, Building, ArrowRight, Sparkles, ClipboardCheck, FileText } from "lucide-react";

function Stat({ icon: Icon, label, value, color, testId }) {
  return (
    <div className="bg-white rounded-3xl border border-brand-purple/10 shadow-sm p-5" data-testid={testId}>
      <div className="flex items-center gap-3">
        <div className={`w-11 h-11 rounded-2xl flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="text-sm text-brand-inkMuted font-body">{label}</div>
          <div className="font-heading text-2xl font-semibold text-foreground">{value}</div>
        </div>
      </div>
    </div>
  );
}

export default function AdminOverviewPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/stats");
        setStats(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const pendingTotal = (stats?.schools_pending || 0) + (stats?.branches_pending || 0);

  return (
    <>
      <div className="mb-8 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-foreground tracking-tight">
            Super Admin Console
          </h1>
          <p className="text-lg text-brand-inkMuted mt-2">
            Hi <span className="font-semibold text-foreground">{user?.name}</span> — here's what's happening across SchoolBoss.
          </p>
        </div>
        <div className="inline-flex items-center gap-2 rounded-full bg-brand-purpleLight px-4 py-1.5 text-sm font-semibold text-brand-purpleHover">
          <Sparkles className="w-4 h-4" /> Platform pulse
        </div>
      </div>

      {loading ? (
        <div className="text-brand-inkMuted font-heading">Loading...</div>
      ) : stats && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Stat icon={ClipboardList} label="Schools pending" value={stats.schools_pending} color="bg-[#FFF3E0] text-[#E65100]" testId="stat-schools-pending" />
            <Stat icon={Building} label="Schools approved" value={stats.schools_approved} color="bg-[#E8F5E9] text-[#2E7D32]" testId="stat-schools-approved" />
            <Stat icon={ClipboardList} label="Branches pending" value={stats.branches_pending} color="bg-[#FFF3E0] text-[#E65100]" testId="stat-branches-pending" />
            <Stat icon={Building2} label="Branches approved" value={stats.branches_approved} color="bg-[#E8F5E9] text-[#2E7D32]" testId="stat-branches-approved" />
          </div>

          <div className="grid sm:grid-cols-2 gap-5 mb-8">
            <div className="bg-white rounded-3xl border border-brand-purple/10 shadow-sm p-6">
              <Users2 className="w-7 h-7 text-brand-purple mb-3" />
              <div className="font-heading text-3xl font-semibold text-foreground">{stats.total_owners}</div>
              <div className="text-brand-inkMuted text-sm">School owners on board</div>
            </div>
            <div className="bg-gradient-to-br from-[#2563EB] to-[#1D4ED8] rounded-3xl shadow-md p-6 text-white">
              <ClipboardCheck className="w-7 h-7 mb-3 opacity-90" />
              <div className="font-heading text-3xl font-semibold">{pendingTotal}</div>
              <div className="text-white/85 text-sm mb-4">Items awaiting your review</div>
              <Link to="/admin/approvals">
                <Button className="rounded-full bg-white text-[#1D4ED8] hover:bg-[#EFF6FF]" data-testid="overview-go-approvals">
                  Open approval queue <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <Link to="/admin/schools-branches" className="bg-white rounded-3xl border border-brand-purple/10 shadow-sm p-6 hover:-translate-y-1 hover:shadow-md transition-all" data-testid="overview-link-schools">
              <Building className="w-7 h-7 text-brand-purple mb-3" />
              <h3 className="font-heading text-lg font-semibold text-foreground">Schools & Branches</h3>
              <p className="text-sm text-brand-inkMuted mt-1">Browse every school and branch on the platform.</p>
            </Link>
            <Link to="/admin/approvals" className="bg-white rounded-3xl border border-brand-purple/10 shadow-sm p-6 hover:-translate-y-1 hover:shadow-md transition-all" data-testid="overview-link-approvals">
              <ClipboardCheck className="w-7 h-7 text-brand-purple mb-3" />
              <h3 className="font-heading text-lg font-semibold text-foreground">Pending Approval</h3>
              <p className="text-sm text-brand-inkMuted mt-1">Review schools and branches waiting for your decision.</p>
            </Link>
            <Link to="/admin/settings" className="bg-white rounded-3xl border border-brand-purple/10 shadow-sm p-6 hover:-translate-y-1 hover:shadow-md transition-all" data-testid="overview-link-site-content">
              <FileText className="w-7 h-7 text-brand-purple mb-3" />
              <h3 className="font-heading text-lg font-semibold text-foreground">Settings</h3>
              <p className="text-sm text-brand-inkMuted mt-1">Edit landing hero, footer contact and legal pages.</p>
            </Link>
          </div>
        </>
      )}
    </>
  );
}
